import { useRef, useState } from 'react';
import type { DemoKey, DemoScript, FingerKey, Fx, RoundProps, ViewProps } from '../kit.js';
import { BoardButton, BoardFrame, INK, RADIUS, STROKE, TYPE, clamp, easier, mono, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import type { Status } from '../kit.js';
import { ERA_SKIN } from '../../art.js';

/**
 * ENGINEER · TWO COILS — both thumbs, one on each coil.
 *
 * The classic RHYTHM board, drawn as the engine it is: the board is two big pads, LEFT and RIGHT
 * (kit `BoardButton`s), each a glass coil wound in copper with a contact plate at its foot. A light
 * runs down a coil; tap that side as it reaches the plate. The side whose light comes next wears
 * the arrow and a lit edge, and each pad warms slowly as its own light nears the plate (a ramp,
 * never a flash). A tap on a side with no light near the plate is the mistake; a light let past is
 * only lost, and another comes. The status line counts the hits: HITS 2 / 5.
 *
 * Round 1 the lights take turns, left, right, left, and come slowly. Round 2 they come a little
 * quicker and still take turns. Round 3 is quicker again, the gaps between lights uneven, and the
 * side no longer a pattern (never more than two in a row on one side).
 *
 * Converted to the kit (kit.tsx): the rules as plain functions, a small view state (`CoilsView`),
 * a pure `View`, the `Board` that plays it on the game clock, and the `demo` the how-to plays.
 */

export type Side = 0 | 1;

export interface CoilLevel {
  /** Hits to clear the round. */
  need: number;
  /** How long a light takes from the top of the left coil to its plate, in ms. */
  travelMs: number;
  /** From one light reaching its plate to the next, in ms: the least and the most. */
  gapMs: readonly [number, number];
  /** How near the plate, either side, a tap still counts. */
  windowMs: number;
  /** How likely the next light is to come down the other coil (1: they always take turns). */
  switchOdds: number;
  /** The most lights in a row down one coil. */
  maxRun: number;
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const COIL_LEVELS: readonly CoilLevel[] = easier(
  [
    { need: 5, travelMs: 2_400, gapMs: [1_250, 1_250], windowMs: 320, switchOdds: 1, maxRun: 1 },
    { need: 5, travelMs: 2_050, gapMs: [950, 1_150], windowMs: 300, switchOdds: 0.6, maxRun: 2 },
  ],
);

/** The right coil runs a touch quicker than the left, so the two lights fall out of step. */
export const COIL_PACE: readonly [number, number] = [1, 0.9];
/** Two lights never reach their plates closer together than this: relaxed thumbs, not a chord. */
export const MIN_GAP_MS = 450;
/** A tap this far outside the window is only early or late, never a mistake. */
export const NEAR_MS = 300;
/** Before the very first light sets off. */
const LEAD_MS = 400;

/** One light on its way down: which coil, and when it reaches the plate. */
export interface CoilPlan {
  side: Side;
  arriveAt: number;
  /** How many lights in a row, this one included, have come down this coil. */
  run: number;
}

export const travelOf = (lv: CoilLevel, side: Side) => lv.travelMs * COIL_PACE[side];

/** The light after `prev` (or the first, from `startAt`). */
export function nextLight(lv: CoilLevel, prev: CoilPlan | null, rand: () => number, startAt = 0): CoilPlan {
  if (!prev) {
    const side: Side = lv.switchOdds >= 1 || rand() < 0.5 ? 0 : 1;
    return { side, arriveAt: startAt + LEAD_MS + travelOf(lv, side), run: 1 };
  }
  const [lo, hi] = lv.gapMs;
  const gap = Math.max(MIN_GAP_MS, lo + (hi - lo) * rand());
  const other = (1 - prev.side) as Side;
  let side: Side = rand() < lv.switchOdds ? other : prev.side;
  if (side === prev.side && prev.run >= lv.maxRun) side = other;
  return { side, arriveAt: prev.arriveAt + gap, run: side === prev.side ? prev.run + 1 : 1 };
}

export interface CoilLight {
  id: number;
  side: Side;
  arriveAt: number;
  hitAt: number | null;
}

export type CoilVerdict = { kind: 'hit'; id: number } | { kind: 'near' } | { kind: 'miss' };

/**
 * What a tap on one side does: hits the light at that side's plate, or is only a little early or
 * late (or a second tap on a light just hit), or is a mistake — no light of that side anywhere near.
 */
export function judgeTap(lights: readonly CoilLight[], side: Side, now: number, windowMs: number): CoilVerdict {
  let best: CoilLight | null = null;
  let near = false;
  for (const l of lights) {
    if (l.side !== side) continue;
    if (l.hitAt !== null) {
      if (now - l.hitAt < NEAR_MS) near = true;
      continue;
    }
    const d = Math.abs(now - l.arriveAt);
    if (d <= windowMs) {
      if (!best || d < Math.abs(now - best.arriveAt)) best = l;
    } else if (d <= windowMs + NEAR_MS) near = true;
  }
  if (best) return { kind: 'hit', id: best.id };
  return near ? { kind: 'near' } : { kind: 'miss' };
}

// -- the view -------------------------------------------------------------------------------------

/** What the status line says after a tap that did not hit, or a light let past. */
export const COIL_NOTE = { none: 0, otherSide: 1, nothing: 2, close: 3, missed: 4 } as const;
type Note = (typeof COIL_NOTE)[keyof typeof COIL_NOTE];

/** Everything the two coils show. */
export interface CoilsView {
  /** The game clock, ms. */
  t: number;
  /** The lights on their way down: which coil, how far down (units, from the board's top), and how bright (1 live, fading once let past, 0 gone). */
  l: readonly (readonly [side: Side, y: number, o: number])[];
  /** Ms since each plate was last struck by a catch, left and right (500 or more: at rest). */
  h: readonly [number, number];
  /** The side whose light comes next (it wears the arrow), or −1. */
  nx: -1 | Side;
  /** Hits so far, and how many to make. */
  n: number;
  of: number;
  /** The last note (COIL_NOTE), while it shows. */
  note: Note;
  /** 0 playing, 1 the engine runs. */
  s: 0 | 1;
  fx?: Fx | undefined;
}

/** The two pads, side by side, filling the board. */
const PAD_W = 148;
const PAD_H = 304;
const PAD_Y = 8;
const PAD_X: readonly [number, number] = [8, 164];
/** The coil is drawn in a 100 × 174 box, this much larger, at the top of its pad. */
const S = 1.5;
const COIL_X = (PAD_W - 100 * S) / 2;
const COIL_Y = 8;
/** In the coil's box: the glass tube's top, the plate (the line), where a light sets off. */
const TUBE_TOP = 6;
const PLATE_Y = 122;
const LIGHT_FROM = 12;
const WINDINGS = Array.from({ length: 13 }, (_, i) => 16 + i * 8);
/** A light `f` of the way down its coil (a little past 1 once let past), in board units. */
export const lightY = (f: number) => q(PAD_Y + COIL_Y + (LIGHT_FROM + Math.min(1.12, f) * (PLATE_Y - LIGHT_FROM)) * S, 1);
/** And back: how far down its coil a light at board height `y` is. */
const lightF = (y: number) => ((y - PAD_Y - COIL_Y) / S - LIGHT_FROM) / (PLATE_Y - LIGHT_FROM);
/** A struck plate dips for this long; its ring opens for this long. */
const PRESS_MS = 320;
const RING_MS = 420;
/** A note shows this long. */
const NOTE_MS = 1_100;

/** One pad: its coil, the lights coming down it, the plate, and its name. */
function Pad({ view, side, id, accent, era, lang, onPress }: { view: CoilsView; side: Side; id: string; accent: string; era: ViewProps<CoilsView>['era']; lang: ViewProps<CoilsView>['lang']; onPress: (() => void) | undefined }) {
  const skin = ERA_SKIN[era];
  const mine = view.l.filter((l) => Math.round(l[0]) === side && l[2] > 0.01);
  const live = mine.filter((l) => l[2] > 0.99);
  // The pad warms over the second half of its light's way down.
  const warm = live.reduce((most, l) => Math.max(most, clamp((lightF(l[1]) - 0.5) / 0.5, 0, 1)), 0);
  const isNext = Math.round(view.nx) === side;
  const since = view.h[side];
  const k = since / PRESS_MS;
  const press = k < 0 || k >= 1 ? 0 : k < 0.25 ? k / 0.25 : 1 - (k - 0.25) / 0.75;
  const ring = since < RING_MS ? since / RING_MS : null;
  const label = side === 0 ? say(lang, 'LEFT', 'VENSTRE') : say(lang, 'RIGHT', 'HØYRE');
  const glowAt = (wy: number) =>
    live.reduce((most, l) => {
      const d = (wy - (l[1] - PAD_Y - COIL_Y) / S) / 11;
      return Math.max(most, Math.exp(-d * d));
    }, 0);

  return (
    <BoardButton x={PAD_X[side]} y={PAD_Y} w={PAD_W} h={PAD_H} label={label} accent={accent} down={press > 0.3} onPress={onPress}>
      <g transform={`translate(${-PAD_W / 2} ${-PAD_H / 2})`} pointerEvents="none">
        {/* the pad warms as its light comes down; the next side's edge is lit */}
        <rect width={PAD_W} height={PAD_H} rx={RADIUS.button} fill={`url(#${id}warm)`} opacity={warm * 0.3} />
        {isNext && <rect width={PAD_W} height={PAD_H} rx={RADIUS.button} fill="none" stroke={accent} strokeWidth={STROKE.line} />}

        <g transform={`translate(${COIL_X} ${COIL_Y}) scale(${S})`}>
          {/* the frame the coil hangs in */}
          <rect x="21" y="4" width="4" height="118" rx="2" fill={`url(#${id}steel)`} opacity=".35" />
          <rect x="75" y="4" width="4" height="118" rx="2" fill={`url(#${id}steel)`} opacity=".35" />
          {/* the windings behind the glass */}
          {WINDINGS.map((wy) => (
            <path key={wy} d={`M70 ${wy + 4} Q50 ${wy + 1} 30 ${wy + 8}`} stroke={`url(#${id}cub)`} strokeWidth="3" fill="none" strokeLinecap="round" />
          ))}
          {/* the glass tube, dark inside */}
          <rect x="34" y={TUBE_TOP} width="32" height={PLATE_Y - TUBE_TOP} rx="16" fill={`url(#${id}core)`} stroke={skin.ink} strokeOpacity=".35" strokeWidth=".8" />
          {/* the lights coming down */}
          {mine.map((l, i) => {
            const y = (l[1] - PAD_Y - COIL_Y) / S;
            return (
              <g key={i} opacity={l[2]}>
                <ellipse cx="50" cy={y - 11} rx="4.5" ry="11" fill={skin.haze} opacity=".22" />
                <circle cx="50" cy={y} r="11" fill={`url(#${id}ball)`} />
                <circle cx="50" cy={y} r="3.2" fill={INK.hi} />
              </g>
            );
          })}
          {/* the windings in front, lit where the light passes */}
          {WINDINGS.map((wy) => {
            const g = glowAt(wy);
            const d = `M30 ${wy} Q50 ${wy + 9} 70 ${wy + 4}`;
            return (
              <g key={wy}>
                <path d={d} stroke={`url(#${id}cu)`} strokeWidth="3.4" fill="none" strokeLinecap="round" />
                {g > 0.04 && <path d={d} stroke={skin.haze} strokeWidth="3.4" fill="none" strokeLinecap="round" opacity={g * 0.85} />}
                <path d={`M31 ${wy - 0.9} Q50 ${wy + 7.6} 69 ${wy + 3}`} stroke="#ffe3c6" strokeWidth=".7" fill="none" opacity=".45" />
              </g>
            );
          })}
          {/* the glass's sheen, and its cap */}
          <rect x="37.5" y="12" width="4.5" height="100" rx="2.2" fill={INK.hi} opacity=".09" />
          <rect x="28" y="2" width="44" height="7" rx="2.5" fill={`url(#${id}steel)`} />
          {/* the contact plate — the line — struck down with each catch */}
          <g transform={`translate(0 ${press * 2.2})`}>
            <ellipse cx="50" cy={PLATE_Y + 2} rx="44" ry="11" fill={`url(#${id}plate)`} opacity={Math.min(1, warm * 0.6 + press * 0.5)} />
            <rect x="15" y={PLATE_Y} width="70" height="8" rx="3" fill={`url(#${id}steel)`} />
            <rect x="15" y={PLATE_Y - 1} width="70" height="2.6" rx="1.3" fill={accent} opacity={0.55 + warm * 0.45} />
            {ring !== null && <ellipse cx="50" cy={PLATE_Y} rx={18 + ring * 30} ry={3 + ring * 6} fill="none" stroke={skin.haze} strokeWidth="1.6" opacity={1 - ring} />}
          </g>
          {/* the piston it drives */}
          <rect x="44" y={PLATE_Y + 8} width="12" height={10 + press * 4} fill={`url(#${id}rod)`} />
          <rect x="30" y={PLATE_Y + 16} width="40" height="13" rx="3" fill="#1b2128" stroke={skin.ink} strokeOpacity=".3" strokeWidth=".8" />
        </g>

        {/* the side's name, big, under the coil; the next side wears the arrow */}
        <path d={`M${PAD_W / 2 - 10} 262 L${PAD_W / 2} 250 L${PAD_W / 2 + 10} 262 Z`} fill={accent} opacity={isNext ? 1 : 0} />
        <text x={PAD_W / 2} y="288" textAnchor="middle" fontFamily={mono} fontSize={TYPE.button} fontWeight="700" letterSpacing="2.5" fill={isNext ? INK.hi : INK.dim}>
          {label}
        </text>
      </g>
    </BoardButton>
  );
}

/** The two coils, from their view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<CoilsView>) {
  const skin = ERA_SKIN[era];
  const id = useSvgId();
  // A demo glides every number between its keys: counts and states are read rounded.
  const n = Math.round(view.n);
  const note = Math.round(view.note) as Note;
  const status: Status =
    Math.round(view.s) === 1
      ? { text: say(lang, '✓ ENGINE RUNNING', '✓ MOTOREN GÅR'), tone: 'ok' }
      : note === COIL_NOTE.otherSide
        ? { text: say(lang, 'OTHER SIDE', 'ANDRE SIDEN'), tone: 'bad' }
        : note === COIL_NOTE.nothing
          ? { text: say(lang, 'NO LIGHT THERE YET', 'INGEN LYS DER ENNÅ'), tone: 'bad' }
          : note === COIL_NOTE.close
            ? { text: say(lang, 'NEARLY — ON THE LINE', 'NESTEN – PÅ STREKEN'), tone: 'warn' }
            : note === COIL_NOTE.missed
              ? { text: say(lang, 'MISSED ONE — NEXT', 'BOM – NESTE'), count: [n, view.of], tone: 'warn' }
              : { text: say(lang, 'HITS', 'TREFF'), count: [n, view.of], hint: say(lang, 'USE BOTH THUMBS', 'BRUK BEGGE TOMLENE') };
  const press = input?.press;

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <linearGradient id={`${id}cu`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffc38f" />
          <stop offset=".45" stopColor="#c0712f" />
          <stop offset="1" stopColor="#5e2f10" />
        </linearGradient>
        <linearGradient id={`${id}cub`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#7c4520" />
          <stop offset="1" stopColor="#35190a" />
        </linearGradient>
        <linearGradient id={`${id}core`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={skin.ink} stopOpacity=".22" />
          <stop offset=".3" stopColor="#04070b" stopOpacity=".95" />
          <stop offset=".7" stopColor="#04070b" stopOpacity=".95" />
          <stop offset="1" stopColor={skin.ink} stopOpacity=".18" />
        </linearGradient>
        <radialGradient id={`${id}ball`}>
          <stop offset="0" stopColor={INK.hi} />
          <stop offset=".32" stopColor={skin.haze} />
          <stop offset=".62" stopColor={accent} stopOpacity=".55" />
          <stop offset="1" stopColor={accent} stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${id}steel`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#eef3f7" />
          <stop offset=".5" stopColor="#8a96a2" />
          <stop offset="1" stopColor="#353d46" />
        </linearGradient>
        <linearGradient id={`${id}rod`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#4a535c" />
          <stop offset=".4" stopColor="#e2e9ef" />
          <stop offset="1" stopColor="#3a424a" />
        </linearGradient>
        <radialGradient id={`${id}plate`}>
          <stop offset="0" stopColor={accent} stopOpacity=".9" />
          <stop offset="1" stopColor={accent} stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${id}warm`} x1="0" y1="0" x2="0" y2="1">
          <stop offset=".35" stopColor={accent} stopOpacity="0" />
          <stop offset="1" stopColor={accent} />
        </linearGradient>
      </defs>
      {([0, 1] as const).map((side) => (
        <Pad key={side} view={view} side={side} id={id} accent={accent} era={era} lang={lang} onPress={press ? () => press(side) : undefined} />
      ))}
    </BoardFrame>
  );
}

// -- the board -----------------------------------------------------------------------------------

interface Live extends CoilLight {
  missed: boolean;
}

function Board(props: RoundProps) {
  const { level, lang } = props;
  const lv = COIL_LEVELS[level]!;
  const [rand] = useState(() => seeded(Date.now() + level * 104_729));
  const lights = useRef<Live[]>([]);
  const plan = useRef<CoilPlan | null>(null);
  const nextId = useRef(1);
  const hits = useRef(0);
  const lastHit = useRef<[number, number]>([-1e9, -1e9]);
  const note = useRef<{ kind: Note; at: number }>({ kind: COIL_NOTE.none, at: -1e9 });

  const round = useRound(props, (_dt, now, won) => {
    if (plan.current === null) plan.current = nextLight(lv, null, rand, now);
    if (!won) {
      // Each light sets off its coil's travel time before it is due at the plate.
      while (now >= plan.current.arriveAt - travelOf(lv, plan.current.side)) {
        const p: CoilPlan = plan.current;
        lights.current.push({ id: nextId.current++, side: p.side, arriveAt: p.arriveAt, hitAt: null, missed: false });
        plan.current = nextLight(lv, p, rand);
      }
    }
    for (const l of lights.current) {
      if (l.hitAt === null && !l.missed && now > l.arriveAt + lv.windowMs) {
        l.missed = true;
        if (!won) note.current = { kind: COIL_NOTE.missed, at: now };
      }
    }
    lights.current = lights.current.filter((l) => now < l.arriveAt + lv.windowMs + 900);
  });

  const tap = (side: Side) => {
    if (!round.live) return;
    const at = round.time();
    const v = judgeTap(lights.current, side, at, lv.windowMs);
    if (v.kind === 'hit') {
      const l = lights.current.find((x) => x.id === v.id);
      if (l) l.hitAt = at;
      lastHit.current[side] = at;
      hits.current += 1;
      note.current = { kind: COIL_NOTE.none, at };
      round.hit();
      round.progress(hits.current / lv.need);
      if (hits.current >= lv.need) round.win();
      return;
    }
    if (v.kind === 'near') {
      // A second tap on the light just caught says nothing; a tap a little early or late says so.
      if (at - lastHit.current[side] >= NEAR_MS) note.current = { kind: COIL_NOTE.close, at };
      return;
    }
    const other = lights.current.some((l) => l.side !== side && l.hitAt === null && Math.abs(at - l.arriveAt) <= lv.windowMs + NEAR_MS);
    note.current = { kind: other ? COIL_NOTE.otherSide : COIL_NOTE.nothing, at };
    round.miss();
  };

  const now = round.now;
  const progressOf = (l: Live) => (now - (l.arriveAt - travelOf(lv, l.side))) / travelOf(lv, l.side);
  // Which side is next: the first light still to reach its plate.
  const waiting = lights.current.filter((l) => l.hitAt === null && !l.missed).sort((a, b) => a.arriveAt - b.arriveAt);
  const view: CoilsView = {
    t: q(now),
    l: lights.current
      .filter((l) => l.hitAt === null)
      .map((l) => [l.side, lightY(progressOf(l)), l.missed ? q(clamp(1 - (now - l.arriveAt - lv.windowMs) / 500, 0, 1), 2) : 1] as const),
    h: [q(Math.min(500, now - lastHit.current[0])), q(Math.min(500, now - lastHit.current[1]))],
    nx: round.won ? -1 : (waiting[0]?.side ?? -1),
    n: hits.current,
    of: lv.need,
    note: now - note.current.at < NOTE_MS ? note.current.kind : COIL_NOTE.none,
    s: round.won ? 1 : 0,
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Two coils: tap the side whose light reaches the line', 'To spoler: trykk på siden der lyset når streken'), press: (side) => tap(side as Side) }}
    />
  );
}

// -- the how-to ----------------------------------------------------------------------------------

/**
 * The how-to, by round 1's own timing: a light down the left coil, caught on the plate; one down
 * the right, caught; one more down the left. Worked out once and keyed every tenth of a second,
 * with each catch keyed on its own moment.
 */
function demoCoils(): DemoScript<CoilsView> {
  const lv = COIL_LEVELS[0]!;
  const ms = 7_000;
  const plan: CoilPlan[] = [nextLight(lv, null, () => 0, -LEAD_MS + 200)];
  while (plan.length < 3) plan.push(nextLight(lv, plan.at(-1)!, () => 0));
  const catches = plan.map((p) => Math.round(p.arriveAt));
  const viewAt = (t: number): Partial<CoilsView> => {
    const done = catches.filter((c) => t > c).length;
    const lastOn = (side: Side) => Math.max(-1e9, ...plan.map((p, i) => (p.side === side && t > catches[i]! ? catches[i]! : -1e9)));
    const next = plan.find((p, i) => t <= catches[i]! && t >= p.arriveAt - travelOf(lv, p.side));
    const since = t - Math.max(lastOn(0), lastOn(1));
    return {
      t: 0,
      l: plan.map((p, i) => {
        const f = (t - (p.arriveAt - travelOf(lv, p.side))) / travelOf(lv, p.side);
        return [p.side, lightY(clamp(f, 0, 1)), f >= 0 && t <= catches[i]! ? 1 : 0] as const;
      }),
      h: [q(Math.min(500, t - lastOn(0))), q(Math.min(500, t - lastOn(1)))],
      nx: next ? next.side : -1,
      n: done,
      of: lv.need,
      note: COIL_NOTE.none,
      s: 0,
      fx: [q(clamp(1 - since / 450, 0, 1), 2), 0],
    };
  };
  const keys: DemoKey<CoilsView>[] = [];
  for (let t = 0; t <= ms; t += 100) keys.push({ at: t, view: viewAt(t) });
  for (const c of catches) {
    if (c % 100 !== 0) keys.push({ at: c, view: viewAt(c) });
    keys.push({ at: c + 10, view: viewAt(c + 10), ease: 'step' });
  }
  keys.sort((a, b) => a.at - b.at);
  const padTip = (side: Side) => ({ x: PAD_X[side] + PAD_W / 2, y: 268 });
  const finger: FingerKey[] = [
    { at: 0, x: 262, y: 304, hide: true },
    { at: 600, x: 262, y: 304, hide: false },
  ];
  plan.forEach((p, i) => {
    const tip = padTip(p.side);
    finger.push({ at: catches[i]! - 500, ...tip }, { at: catches[i]!, ...tip, down: true }, { at: catches[i]! + 150, ...tip, down: false });
  });
  finger.push({ at: ms - 700, x: 262, y: 304, hide: true });
  return {
    ms,
    keys,
    finger,
    steps: [
      { at: 0, en: 'WATCH THE LIGHT COME DOWN', no: 'SE LYSET KOMME NED' },
      { at: catches[0]! - 700, en: 'TAP ITS SIDE AT THE LINE', no: 'TRYKK DEN SIDEN VED STREKEN' },
      { at: catches[1]! - 900, en: 'USE BOTH THUMBS', no: 'BRUK BEGGE TOMLENE' },
    ],
  };
}

export const game = thirtyGame<CoilsView>({
  Board,
  View,
  demo: demoCoils(),
  words: {
    en: {
      instruction: 'Both thumbs: tap the side whose light reaches the line. Three rounds, each quicker.',
      howMove: 'Left thumb on LEFT, right thumb on RIGHT. Tap a side as its light reaches the line.',
    },
    no: {
      instruction: 'Begge tomler: trykk på siden der lyset når streken. Tre runder, stadig raskere.',
      howMove: 'Venstre tommel på VENSTRE, høyre på HØYRE. Trykk når lyset når streken.',
    },
  },
});
